<template lang="pug">
div
  UiStack(vertical)
    RestTable(
      ref="table"
      namespace="invoices"
      :headers="headers"
      :fetchHandler="getData"
      :initialFilters="initialFilters"
      embed
    )
      template(#filters="{ filters, updateFilters }")
        UiStack
          UiStackItem
            UiStack
              UiStackItem
                label(label-for="search") {{ $t("labels.search") }}
              UiStackItem
                UiInput.filter-input(
                  :modelValue="filters.search"
                  @update:modelValue="updateFilters({ search: $event || undefined })"
                  :placeholder="$t('placeholders.type')"
                )
          UiStackItem
            UiStack
              UiStackItem
                label(label-for="status") {{ $t("labels.status") }}
              UiStackItem
                UiSelect.filter-input(
                  :searchable="false"
                  :options="statusOptions"
                  :modelValue="filters.status"
                  @update:modelValue="updateFilters({ status: $event || undefined })"
                )
          UiStackItem
            UiStack
              UiStackItem
                label(label-for="status") {{ $t("labels.files") }}
              UiStackItem
                DigiaFileSelect.filter-input(
                  multiselect
                  :companyId="globalRequestFilters.company_id"
                  :dateStart="globalRequestFilters.date_start"
                  :dateEnd="globalRequestFilters.date_end"
                  @update:modelValue="updateFilters({ file_ids: $event })"
                  :modelValue="filters.file_ids"
                )
          UiStackItem(fill)
          UiStackItem
            UiButton(
              variant="primary"
              @click="callExportFailed"
              icon="FileDownload"
            ) {{ $t("actions.export_failed_invoices_numbers") }}
          UiStackItem
            UiButton(
              variant="primary"
              @click="callRestartDialog"
              icon="RefreshCw"
            ) {{ $t("actions.restart_all_failed") }}
      template(v-slot:cel_uploaded="{ item }")
        InvoiceUploadedStatus(:success="item.uploaded")
      template(v-slot:cel_status="{ item }")
        InvoiceStatusBadge(:status="item.status")
      template(v-slot:cel_invoice_number="{ item }")
        a(
          href="#"
          @click="showInvoiceRecord($event, item)"
        ) {{ item.invoice_number }}
      template(v-slot:cel_filename="{ item }")
        RouterLink(:to="getFileFilterLink(item)") {{ item.filename }}
  ShowInvoiceRecordModal(ref="showInvoiceRecordModal")
</template>

<script>
import ApiDigiaSalesInvoices from "@/api/digia_sales_invoices.js";
import InvoiceUploadedStatus from "./components/invoice_uploaded_status.vue";
import InvoiceStatusBadge from "./components/invoice_status_badge.vue";
import DigiaFileSelect from "@/components/DigiaFileSelect.vue";
import { saveResponseAsFile } from "@/common/save_response_as_file.js";
import ShowInvoiceRecordModal from "@/modals/Digia/showInvoiceRecord.vue";
import isEqual from "lodash/isEqual";

export default {
  components: {
    InvoiceStatusBadge,
    InvoiceUploadedStatus,
    DigiaFileSelect,
    ShowInvoiceRecordModal,
  },

  props: {
    globalRequestFilters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      initialFilters: {
        status: null,
        files: [],
      },
    };
  },

  computed: {
    headers() {
      return [
        {
          key: "id",
          label: this.$t("labels.id"),
          sortable: true,
          squeeze: true,
          wrap: false,
        },
        {
          key: "invoice_number",
          label: this.$t("labels.invoice_number"),
          sortable: true,
          squeeze: true,
          wrap: false,
        },
        {
          key: "filename",
          label: this.$t("labels.file_name"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("labels.date"),
          sortable: true,
        },
        {
          key: "uploaded",
          label: this.$t("labels.upload"),
          sortable: true,
          squeeze: true,
        },
        {
          key: "customer_number",
          label: this.$t("labels.customer_number"),
          sortable: true,
          wrap: false,
        },
        {
          key: "netvisor_key",
          label: this.$t("labels.netvisor_key"),
          sortable: true,
        },
        {
          key: "status",
          label: this.$t("labels.status"),
          sortable: true,
        },
        {
          key: "status_reason",
          label: this.$t("labels.status_reason"),
          width: "35%",
        },
      ];
    },

    statusOptions() {
      const statuses = [
        "draft",
        "processing",
        "waiting_for_customer",
        "processed",
        "duplicate",
        "failed",
      ].map((status) => ({
        value: status,
        label: this.$t(`labels.${status}`),
      }));

      return [
        {
          value: null,
          label: this.$t("labels.any_status"),
        },
        ...statuses,
      ];
    },
  },

  watch: {
    globalRequestFilters: {
      handler(newValue, oldValue) {
        if (isEqual(newValue, oldValue)) return;

        this.refetchTable();
      },
      deep: true,
    },
  },

  methods: {
    refetchTable() {
      this.$refs.table.fetchData();
    },

    getData(params, config) {
      return ApiDigiaSalesInvoices.fetchAll(
        {
          ...params,
          ...this.globalRequestFilters,
        },
        config,
      ).then((response) => {
        return {
          items: response.data.sales_invoices,
          meta: response.data.meta,
        };
      });
    },

    callExportFailed() {
      ApiDigiaSalesInvoices.exportFailed({
        ...this.globalRequestFilters,
      }).then((response) => {
        saveResponseAsFile(response, "failed_invoices.xlsx");
      });
    },

    callRestartDialog() {
      this.$dialogConfirm({
        title: "Restart all failed",
        description: "Do you really want to restart all failed invoices?",
        variant: "danger",
        okLabel: this.$t("actions.restart_all_failed"),
      }).then((result) => {
        if (!result) return;
        ApiDigiaSalesInvoices.restart({
          ...this.globalRequestFilters,
        }).then(() => {
          this.refetchTable();
        });
      });
    },

    getFileFilterLink(item) {
      return {
        ...this.$route,
        query: {
          ...this.$route.query,
          files: JSON.stringify([
            { value: item.file_id, label: item.filename },
          ]),
        },
      };
    },

    showInvoiceRecord(event, item) {
      event.preventDefault();
      this.$refs.showInvoiceRecordModal.open(item);
    },
  },
};
</script>
